<template>
  <div class="mb-5">
    <h2 class="text-2xl font-bold text-gray-500" v-html="article.title"></h2>

    <h3 v-if="article.subtitle" class="mt-2.5 text-base font-bold text-gray-500" v-html="article.subtitle"></h3>
  </div>

  <div class="flex justify-end">
    <div class="flex items-center gap-2">
      <div class="w-[12px] h-[28px]" :style="{ 'backgroundColor': props.article?.category?.meta?.color ?? '#000000' }"></div>
      <div class="text-xl font-bold" :style="{ 'color': article?.category?.meta?.color ?? '#000000' }">{{ article?.category?.name }}</div>
    </div>
  </div>

  <div v-if="article.meta.doi" class="text-right truncate mt-2">
    <a :href="'https://doi.org/' + article.meta.doi" class="text-xs font-medium text-ch-red hover:underline">{{ !article.meta.doi.includes('DOI:') ? 'DOI: ' : '' }}{{ article.meta.doi }}</a>
  </div>

	<div v-if="article.leading_content" class="my-6">
		<p class="font-medium mb-3">Authors</p>
		<p class="text-base font-medium text-gray-600" v-html="article.leading_content"></p>
    <p v-if="article.leading_content_2" class="text-sm font-medium text-gray-600 mt-2" v-html="article.leading_content_2"></p>
	</div>

	<div v-if="article.summary" class="my-6">
		<p class="font-medium mb-3">Summary</p>
		<p class="wysiwyg-content wysiwyg-content-sm" v-html="article.summary"></p>
	</div>

  <div v-if="article.summary_hu" class="my-6">
    <p class="font-medium mb-3">Összefoglaló</p>
    <p class="wysiwyg-content wysiwyg-content-sm" v-html="article.summary_hu"></p>
  </div>

  <div v-if="article.keywords_en" class="my-6">
    <p class="font-medium mb-3">Keywords</p>
    <p class="text-xs font-medium text-gray-600">{{ article.keywords_en }}</p>
  </div>

  <div v-if="article.keywords_hu" class="my-6">
    <p class="font-medium mb-3">Kulcsszavak</p>
    <p class="text-xs font-medium text-gray-600">{{ article.keywords_hu }}</p>
  </div>

  <div id="issue" class="my-6">
    <p class="font-medium mb-3">Cite / Idézés</p>
    <p class="text-xs font-medium text-gray-600" v-html="cite"></p>
  </div>

	<div class="flex flex-col flex-wrap justify-between gap-5 mb-6 sm:flex-row">
		<button v-if="!expanded" @click="expand" class="rounded-md transition-all bg-ch-red hover:bg-ch-dark-red-100 text-white py-1.5 px-5 text-base w-full sm:w-fit">
			Read the article
		</button>
		
		<div class="flex flex-wrap gap-3 gap-x-4">
      <a v-if="article.meta.pdf" :href="article.meta.pdf" target="_blank" class="text-center sm:text-left rounded-md transition-all border-2 border-ch-red hover:text-white hover:bg-ch-red text-ch-red py-1.5 px-5 text-sm w-full sm:w-fit">
        <font-awesome-icon class="mr-2" icon="fa-solid fa-file-pdf" />
        Download the PDF
        <sup class="ml-1"><font-awesome-icon style="font-size: .6rem" icon="fa-solid fa-arrow-up-right-from-square" /></sup>
      </a>
      <a v-if="article.meta.supplementum" :href="article.meta.supplementum" target="_blank" class="text-center sm:text-left rounded-md transition-all border-2 border-ch-red hover:text-white hover:bg-ch-red text-ch-red py-1.5 px-5 text-sm w-full sm:w-fit">
        <font-awesome-icon class="mr-2" icon="fa-solid fa-file-pdf" />
        Download the Supplementum
        <sup class="ml-1"><font-awesome-icon style="font-size: .6rem" icon="fa-solid fa-arrow-up-right-from-square" /></sup>
      </a>
			<button type="button" v-if="article.meta.video" @click="() => openPlayer(article.meta.video)" class="text-center sm:text-left rounded-md transition-all border-2 border-ch-red hover:text-white hover:bg-ch-red text-ch-red py-1.5 px-5 text-sm w-full sm:w-fit">
				<font-awesome-icon class="mr-2" icon="fa-solid fa-video" />
				Watch the video summary
			</button>
			<button type="button" v-if="article.meta.live" @click="() => openPlayer(article.meta.live)" class="text-center sm:text-left rounded-md transition-all border-2 border-ch-red hover:text-white hover:bg-ch-red text-ch-red py-1.5 px-5 text-sm w-full sm:w-fit">
				<font-awesome-icon class="mr-2" icon="fa-solid fa-tower-broadcast" />
				Watch CH Live interview
      </button>
		</div>
	</div>

  <div
      v-if="playerIframeSrc"
      class="flex justify-center mb-6"
      ref="playerContainerRef"
  >
    <div class="w-full sm:w-2/3">
      <div :class="!playerContainerIsIntersecting && 'fixed bottom-28 right-10 xl:right-8 z-1 w-[240px] h-auto'">
        <div
            v-if="!playerContainerIsIntersecting"
            class="flex justify-end"
        >
          <button
              type="button"
              class="flex items-center justify-center w-11 p-1 bg-ch-dark-red-100 hover:bg-ch-dark-red-200 transition-all rounded-t-lg shadow-sm text-white text-xl"
              @click="closePlayer"
          >
            <font-awesome-icon icon="fa-solid fa-close" />
          </button>
        </div>

        <div class="relative w-full overflow-hidden pt-[56.25%]">
          <iframe
              class="absolute top-0 left-0 bottom-0 right-0 w-full h-full border-0"
              :src="playerIframeSrc"
              allowfullscreen
          />
        </div>
      </div>

      <div
          v-if="!playerContainerIsIntersecting"
          class="relative w-full overflow-hidden pt-[56.25%]"
      >
        <div class="absolute top-0 left-0 bottom-0 right-0 w-full h-full"></div>
      </div>
    </div>
  </div>

	<transition name="article-expanded">
		<div class="break-words article-inside" v-if="expanded">
			<div class="wysiwyg-content lightbox-enabled-container" v-html="article.content"></div>

			<div v-if="article.tailing_content" class="mt-6">
				<p class="mb-2"><b class="font-medium">References</b></p>
				<p class="wysiwyg-content wysiwyg-content-sm" :class="[article?.tailing_content?.includes('</p>') || article?.tailing_content?.includes('</div>') ? '' : 'whitespace-pre-line']" v-html="article.tailing_content"></p>
			</div>
		</div>
	</transition>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useMeta } from '@/composables/useMeta.js'

const props = defineProps({
  article: {
    type: Object,
    required: true
  }
})

const {
  getTextExcerptFromHTML,
  extractAuthorsFromHTML,
  formatTimestamp
} = useMeta()

useHead({
  title: props.article.title ?? '',
  titleTemplate: '%s | ' + import.meta.env.VITE_SITE_TITLE ?? '',
  meta: [
    // Dublin Core
    { name: 'DC.Title', content: props.article.title ?? '' },
    { name: 'DC.Creator', content: extractAuthorsFromHTML(props.article.leading_content ?? '').join('; ') },
    { name: 'DC.Subject', content: props.article.keywords_en ?? props.article.keywords ?? '' },
    { name: 'DC.Description', content: getTextExcerptFromHTML(props.article.summary ?? '', 500) ?? '' },
    { name: 'DC.Publisher', content: 'Promenade Publishing House' },
    { name: 'DC.Date', content: formatTimestamp(props.article.visible, '-') },
    { name: 'DC.Type', content: 'Text' },
    { name: 'DC.Format', content: 'text/html' },
    { name: 'DC.Identifier.DOI', scheme: 'DOI', content: props.article.meta?.doi ?? '' },
    { name: 'DC.Identifier', scheme: 'DOI', content: props.article.meta?.doi ?? '' },
    { name: 'DC.Identifier', content: window.location.href },
    { name: 'DC.Language', content: 'en' },
    { name: 'DC.Rights', content: 'Copyright © Promenade Publishing House' },

    // Highwire Press
    { name: 'citation_title', content: props.article.title ?? '' },
    { name: 'citation_author', content: extractAuthorsFromHTML(props.article.leading_content ?? '').join('; ') },
    { name: 'citation_publication_date', content: formatTimestamp(props.article.visible, '/') },
    { name: 'citation_journal_title', content: 'Cardiologia Hungarica' },
    { name: 'citation_language', content: 'en' },
    { name: 'citation_keywords', content: props.article.keywords_en ?? props.article.keywords ?? '' },
    { name: 'citation_volume', content: props.article.meta?.volume ?? '' },
    { name: 'citation_issue', content: props.article.meta?.issue ?? '' },
    { name: 'citation_year', content: props.article.meta?.year ?? '' },
    { name: 'citation_doi', content: props.article.meta?.doi ?? '' },
    { name: 'citation_abstract', content: getTextExcerptFromHTML(props.article.summary ?? '', 500) ?? '' },
    { name: 'citation_pdf_url', content: props.article.meta?.pdf ?? '' },
    { name: 'citation_public_url', content: window.location.href },

    // Open Graph
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: props.article.title ?? '' },
    { property: 'og:description', content: getTextExcerptFromHTML(props.article.summary ?? '', 500) ?? '' },
    { property: 'og:url', content: window.location.href },
    { property: 'og:image', content: 'https://cardiologia.hungarica.eu/assets/ch_og.png' },
    { property: 'og:site_name', content: 'Cardiologia Hungarica' },
    { property: 'og:article:published_time', content: formatTimestamp(props.article.visible, '-') },
    { property: 'og:article:author', content: extractAuthorsFromHTML(props.article.leading_content ?? '').join('; ') },
    { property: 'og:article:tag', content: props.article.keywords_en ?? props.article.keywords ?? '' },
    { property: 'og:locale', content: 'en_US' },
  ]
})

const route = useRoute()

const expanded = ref(true)

const playerContainerRef = ref(null)
const playerIframeSrc = ref(null)
const playerObserver = ref(null)
const playerContainerIsIntersecting = ref(false)

const cite = computed(() => {
  let output = ''

  const authors = (props.article?.citation_authors ?? '').replace(/[.,;]+$/, '')

  if (authors) {
    output += authors + '. '
  }

  if (props.article?.title) {
    output += props.article.title + '. '
  }

  output += 'Card Hung. ';

  if (props.article?.meta?.year) {
    output += props.article.meta.year

    if (props.article?.meta?.volume) {
      output += ';' + props.article.meta.volume

      if (props.article?.meta?.issue) {
        output += '(' + props.article.meta.issue + ')'
      }

      if (props.article?.meta?.pages) {
        output += ':' + props.article.meta.pages
      }
    }

    output += '. '
  }

  if (props.article?.meta?.doi) {
    // const doiUrl = 'https://doi.org/' + props.article.meta.doi

    output += 'doi:' + props.article.meta.doi
  }

  output = output.trim()

  return output
})

function expand() {
  expanded.value = true

  history.pushState(
      {}, null, route.path + '?article=open'
  )
}

async function openPlayer(src) {
  playerIframeSrc.value = src

  await nextTick()

  playerObserver.value = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        playerContainerIsIntersecting.value = true
      } else {
        playerContainerIsIntersecting.value = false
      }
    })
  })

  if (playerContainerRef.value) {
    playerObserver.value.observe(playerContainerRef.value)
  }
}

function closePlayer() {
  playerIframeSrc.value = null

  if (playerObserver.value) {
    playerObserver.value.disconnect()
  }
}

// Init expanded state
if (route.query?.article === 'closed') {
  expanded.value = false
}

// Scrolling to issue section on page load
onMounted(() => {
  if (route.query?.scroll === 'issue') {
    document.getElementById('issue').scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
})

// Scrolling to anchor (hash) on page load
onMounted(async () => {
  const hash = window.location.hash

  if (hash) {
    await nextTick()

    const el = document.querySelector(hash)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }
})

onUnmounted(() => {
  if (playerObserver.value) {
    playerObserver.value.disconnect()
  }
})
</script>

<style scoped>
.article-expanded-enter-active,
.article-expanded-leave-active {
  transition: all 500ms ease-out;
}

.article-expanded-enter-to {
  opacity: 1;
}

.article-expanded-enter-from {
  opacity: 0;
}

.article-expanded-leave-to {
  opacity: 0;
}

.article-expanded-leave-from {
  opacity: 1;
}
</style>
